var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { LoggingLevel } from '@augloop/interfaces';
var serviceName = ((typeof process != 'undefined') && process.env) ? process.env.SERVICE_NAME : 'client';
var symbolSpace = 'abcdefghijklmnopqrstuvwxyz0123456789';
var symbolSpaceLookup = {
    97: 0,
    98: 1,
    99: 2,
    100: 3,
    101: 4,
    102: 5,
    103: 6,
    104: 7,
    105: 8,
    106: 9,
    107: 10,
    108: 11,
    109: 12,
    110: 13,
    111: 14,
    112: 15,
    113: 16,
    114: 17,
    115: 18,
    116: 19,
    117: 20,
    118: 21,
    119: 22,
    120: 23,
    121: 24,
    122: 25,
    48: 26,
    49: 27,
    50: 28,
    51: 29,
    52: 30,
    53: 31,
    54: 32,
    55: 33,
    56: 34,
    57: 35
};
var loggers = [];
var decidingLoggers = [];
var getCorrelation;
var aggregators = new Map();
var loggersByLevel = new Map();
var decidingLoggersByLevel = new Map();
var overridenTags = new Map();
export var Log;
(function (Log) {
    Log.defineCoreLogCategory = function (name) {
        return { root: 'Core', name: name };
    };
    Log.defineWorkflowLogCategory = function (name) {
        return { root: 'Workflow', name: name };
    };
    Log.clearLoggers = function () {
        loggers = [];
        decidingLoggers = [];
        loggersByLevel.clear();
        decidingLoggersByLevel.clear();
    };
    Log.addLogger = function (logger) {
        if (loggers.indexOf(logger) !== -1) {
            return;
        }
        loggers.push(logger);
        pushToMapByLevel(loggersByLevel, logger);
    };
    Log.addDecidingLogger = function (logger) {
        if (decidingLoggers.indexOf(logger) !== -1) {
            return;
        }
        decidingLoggers.push(logger);
        pushToMapByLevel(decidingLoggersByLevel, logger);
    };
    Log.setCorrelationContextCallback = function (cb) {
        getCorrelation = cb;
    };
    Log.addAggregator = function (aggregator) {
        aggregator.init(function (obj, isAggregated) {
            logToLoggers(obj, isAggregated);
        });
        aggregators.set(aggregator.eventName, aggregator);
    };
    Log.flushAggregators = function (stop) {
        aggregators.forEach(function (aggregator) {
            aggregator.flush(stop);
        });
    };
    /**
     * Log a specific tag at a different level
     * @param tag the string encoded log tag. Use {@link tagIdToString} to convert from its integer form
     * @param level The new level to log the tag at.
     *
     * @example
     * const tag = 0x13371337;
     * Log.setTagLevelOverride(tagIdToString(tag), LoggingLevel.Error);
     * Log.info(tag, LogCategory.WorkflowDefault, 'This is logged at error level!');
     */
    Log.setTagLevelOverride = function (tag, level) {
        var tagId = tagStringToId(tag);
        overridenTags.set(tagId, level);
    };
    Log.resetTagLevelOverrides = function () {
        overridenTags.clear();
    };
    /** Logs a message at the `error` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.error(0, LogCategory.WorkflowDefault, 'Hello world');
     */
    Log.error = function (tagId, logCategory, a, b, c, d, e, f, g, h) {
        log(tagId, logCategory, LoggingLevel.error, a, b, c, d, e, f, g, h);
    };
    /** Logs a message at the `warn` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.warn(0, LogCategory.WorkflowDefault, 'Hello world');
     */
    Log.warn = function (tagId, logCategory, a, b, c, d, e, f, g, h) {
        log(tagId, logCategory, LoggingLevel.warn, a, b, c, d, e, f, g, h);
    };
    /** Logs a message at the `info` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.info(0, LogCategory.WorkflowDefault, 'Hello world');
     */
    Log.info = function (tagId, logCategory, a, b, c, d, e, f, g, h) {
        log(tagId, logCategory, LoggingLevel.info, a, b, c, d, e, f, g, h);
    };
    /** Logs a message at the `verbose` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.verbose(0, LogCategory.WorkflowDefault, 'Hello world');
     */
    Log.verbose = function (tagId, logCategory, a, b, c, d, e, f, g, h) {
        log(tagId, logCategory, LoggingLevel.verbose, a, b, c, d, e, f, g, h);
    };
    /** Logs a message at the `debug` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.debug(0, LogCategory.WorkflowDefault, 'Hello world');
     */
    Log.debug = function (tagId, logCategory, a, b, c, d, e, f, g, h) {
        log(tagId, logCategory, LoggingLevel.debug, a, b, c, d, e, f, g, h);
    };
    /** Logs a message at the `metric` level.
     * @note Set `tagId` to 0 and let the pipeline assign a correct tag.
     *
     * @example <caption>Unstructured log</caption>
     * Log.metric(0, LogCategory.WorkflowDefault, 'Hello world');
     */
    Log.metric = function (tagId, logCategory, a, b, c, d, e, f, g, h) {
        log(tagId, logCategory, LoggingLevel.metric, a, b, c, d, e, f, g, h);
    };
    Log.dynamic = function (obj) {
        logToLoggers(obj);
    };
})(Log || (Log = {}));
var pushToMapByLevel = function (map, logger) {
    var targetLevel = logger.level;
    // InternetExplorer does not support `Object.values`, emulating via `keys.map`
    Object.keys(LoggingLevel)
        .map(function (key) { return LoggingLevel[key]; })
        .filter(function (level) { return typeof level !== 'string'; })
        .filter(function (level) { return level <= targetLevel; })
        .forEach(function (level) {
        if (!map.has(level)) {
            map.set(level, [logger]);
        }
        else {
            map.get(level)
                .push(logger);
        }
    });
};
var unfoldArgs = function (a, b, c, d, e, f, g, h) {
    var e_1, _a;
    // Optimize for the case of a single argument so as not to construct arrays unnecessarily
    if (b === undefined && (typeof a === 'string' || typeof a === 'object')) {
        return a;
    }
    if (b === undefined && typeof a === 'function') {
        return a();
    }
    var result = [];
    try {
        for (var _b = __values([a, b, c, d, e, f, g, h]), _c = _b.next(); !_c.done; _c = _b.next()) {
            var arg = _c.value;
            if (arg !== undefined) {
                result.push(typeof arg === 'function' ? arg() : arg);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return result;
};
export var argsToString = function (args) {
    if (typeof args === 'string') {
        return args;
    }
    return args.map(function (arg) {
        if (arg instanceof Error) {
            return JSON.stringify({
                message: arg.message,
                name: arg.name,
                stack: arg.stack
            });
        }
        if (typeof arg === 'object') {
            return JSON.stringify(arg);
        }
        return arg;
    }).join(' ');
};
var getLoggers = function (level) {
    if (level !== undefined) {
        return loggersByLevel.get(level) || [];
    }
    return loggers;
};
var getDecidingLoggers = function (level, firstArg) {
    var loggers = level !== undefined ? (decidingLoggersByLevel.get(level) || []) : decidingLoggers;
    return loggers.filter(function (logger) { return logger.shouldLog(firstArg); });
};
var log = function (tagId, category, level, a, b, c, d, e, f, g, h) {
    // Expensive log statements will be logged with () => '' syntax and should not be unnecessarily unfolded.
    level = overridenTags.get(tagId) || level;
    var normalLoggers = getLoggers(level);
    var decidingLoggers = getDecidingLoggers(level, a);
    if (normalLoggers.length == 0 && decidingLoggers.length == 0) {
        return;
    }
    var tag = tagIdToString(tagId);
    var unfoldedArgs = unfoldArgs(a, b, c, d, e, f, g, h);
    if (!Array.isArray(unfoldedArgs) && typeof unfoldedArgs === 'object') {
        var obj = unfoldedArgs;
        obj.tagId = tag;
        if (obj.eventName === 'Operation' && category.root === 'Workflow') {
            obj.eventName = 'WorkflowOperation';
            if (getCorrelation) {
                obj.joinContextId = getCorrelation().joinContextId;
            }
        }
        obj.traceLevel = level;
        var aggregator = aggregators.get(obj.eventName);
        if (aggregator) {
            aggregator.add(obj);
        }
        else {
            logToLoggers(obj, false, normalLoggers, decidingLoggers);
        }
    }
    else {
        var metrics = {};
        if (category.root === 'Core') {
            metrics.TraceEventV2 = {
                dimensionNames: function () { return ['Level', 'Tag']; },
                dimensionValues: function () { return [String(level), tag]; },
                value: function () { return 1; }
            };
        }
        else {
            metrics.WorkflowTraceEvent = {
                dimensionNames: function () { return ['Level', 'Tag', 'Workflow']; },
                dimensionValues: function () { var _a; return [String(level), tag, getCorrelation ? (_a = getCorrelation()) === null || _a === void 0 ? void 0 : _a.workflow : '']; },
                value: function () { return 1; }
            };
        }
        logToLoggers({
            eventName: 'Log',
            tagId: tag,
            category: category.root + "." + category.name,
            traceLevel: level,
            message: argsToString(unfoldedArgs),
            metrics: metrics
        }, false, normalLoggers, decidingLoggers);
    }
};
var logToLoggers = function (obj, isAggregated, loggers, moreLoggers) {
    var e_2, _a, e_3, _b;
    var _c, _d, _e;
    if (isAggregated === void 0) { isAggregated = false; }
    if (loggers == null) {
        loggers = getLoggers(obj.traceLevel);
    }
    moreLoggers = moreLoggers || [];
    if (loggers.length > 0 || moreLoggers.length > 0) {
        obj.serviceName = serviceName;
        if (!isAggregated && getCorrelation) {
            var correlation = getCorrelation();
            if (correlation) {
                obj.cv = obj.cv ? obj.cv : correlation.cv.toString();
                obj.sessionKey = obj.sessionKey ? obj.sessionKey : correlation.sessionKey;
                obj.workflow = obj.workflow ? obj.workflow : correlation.workflow;
                /*
                 * Some incoming objects already include separate clientMetadata fields (i.e. clientAppName).
                 * Adding the whole clientMetadata would increase the log size and also cause a cardinality explosion,
                 * as some fields are long, and other high-cardinality.
                 *
                 * Only add short, low cardinality fields to logs.
                 */
                var clientAppName = (_c = correlation.clientMetadata) === null || _c === void 0 ? void 0 : _c.appName;
                obj.clientAppName = obj.clientAppName ? obj.clientAppName : clientAppName;
                var clientAppPlatform = (_d = correlation.clientMetadata) === null || _d === void 0 ? void 0 : _d.appPlatform;
                obj.clientAppPlatform = obj.clientAppPlatform ? obj.clientAppPlatform : clientAppPlatform;
                var clientReleaseAudienceGroup = (_e = correlation.clientMetadata) === null || _e === void 0 ? void 0 : _e.releaseAudienceGroup;
                obj.clientReleaseAudienceGroup = obj.clientReleaseAudienceGroup ? obj.clientReleaseAudienceGroup : clientReleaseAudienceGroup;
            }
        }
        try {
            for (var loggers_1 = __values(loggers), loggers_1_1 = loggers_1.next(); !loggers_1_1.done; loggers_1_1 = loggers_1.next()) {
                var logger = loggers_1_1.value;
                logger.log(obj);
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (loggers_1_1 && !loggers_1_1.done && (_a = loggers_1.return)) _a.call(loggers_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
        try {
            for (var moreLoggers_1 = __values(moreLoggers), moreLoggers_1_1 = moreLoggers_1.next(); !moreLoggers_1_1.done; moreLoggers_1_1 = moreLoggers_1.next()) {
                var logger = moreLoggers_1_1.value;
                logger.log(obj);
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (moreLoggers_1_1 && !moreLoggers_1_1.done && (_b = moreLoggers_1.return)) _b.call(moreLoggers_1);
            }
            finally { if (e_3) throw e_3.error; }
        }
    }
};
/**
 * converts a generated numberic tag to its encoded form to be used in logs
 *
 * @note Do not assign numeric tags manually - use `0` as a placeholder and let
 * the pipeline assign correct tags.
 */
export var tagIdToString = function (tagId) {
    // 0x3F covers a base64 range, but only safe tags should ever be generated
    return symbolSpace[(tagId >> 24) & 0x3F]
        + symbolSpace[(tagId >> 18) & 0x3F]
        + symbolSpace[(tagId >> 12) & 0x3F]
        + symbolSpace[(tagId >> 6) & 0x3F]
        + symbolSpace[(tagId >> 0) & 0x3F];
};
var tagStringToId = function (tag) {
    if (tag && tag.length === 5) {
        return (symbolSpaceLookup[tag.charCodeAt(0)] << 24
            | symbolSpaceLookup[tag.charCodeAt(1)] << 18
            | symbolSpaceLookup[tag.charCodeAt(2)] << 12
            | symbolSpaceLookup[tag.charCodeAt(3)] << 6
            | symbolSpaceLookup[tag.charCodeAt(4)]);
    }
    return -1;
};
var LogCategory = /** @class */ (function () {
    function LogCategory() {
    }
    LogCategory.CoreDefault = Log.defineCoreLogCategory('Default');
    LogCategory.WorkflowDefault = Log.defineWorkflowLogCategory('Default');
    return LogCategory;
}());
export { LogCategory };
//# sourceMappingURL=logging.js.map